import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, CheckCircle2, Eye, EyeOff, Ticket } from "lucide-react";

export default function JoinOrganization() {
  const { orgId } = useParams();
  const [sp] = useSearchParams();
  const [org, setOrg] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ first_name: "", last_name: "", email: sp.get("email") || "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/public/organizations/${orgId}`).then((r)=>setOrg(r.data))
      .catch((e)=>setErr(e?.response?.data?.detail || "Organisme introuvable."));
  }, [orgId]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error("Mot de passe : 8 caractères minimum."); return; }
    setBusy(true);
    try {
      await api.post("/public/members/register", { ...f, org_id: orgId });
      setDone(true);
      setTimeout(()=>nav("/connexion"), 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusy(false); }
  };

  if (err) return <div className="max-w-md mx-auto py-20 px-4 text-center"><h1 className="text-2xl font-bold text-rose-700">Erreur</h1><p className="mt-3 text-slate-600">{err}</p><Link to="/" className="mt-4 inline-block text-[#E05638]">← Accueil</Link></div>;
  if (!org) return <div className="max-w-md mx-auto py-20 px-4 text-center text-slate-500">Chargement...</div>;
  if (done) return (
    <div className="max-w-md mx-auto py-16 px-4 text-center" data-testid="join-org-done">
      <CheckCircle2 size={48} className="mx-auto text-emerald-600"/>
      <h1 className="mt-4 font-heading text-2xl font-extrabold">Bienvenue !</h1>
      <p className="mt-2 text-slate-600">Vous êtes maintenant membre de <b>{org.name}</b>. Redirection vers la connexion...</p>
    </div>
  );
  return (
    <div className="max-w-lg mx-auto py-10 px-4" data-testid="join-org-page">
      <div className="flex items-center gap-3">
        <Building2 className="text-emerald-700" size={28}/>
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Rejoindre</div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold" data-testid="join-org-title">{org.name}</h1>
          <div className="text-xs text-slate-500">{org.type_label}</div>
        </div>
      </div>
      {org.auto_attach_by_domain && org.email_domain && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-900" data-testid="join-org-domain-notice">
          Cet organisme accepte les inscriptions avec un email <b>@{org.email_domain}</b>.
        </div>
      )}
      {org.helloasso_url && (
        <a href={org.helloasso_url} target="_blank" rel="noopener noreferrer" className="mt-4 block" data-testid="join-org-helloasso">
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"><Ticket className="w-4 h-4 mr-1.5" /> Adhérer / Billetterie (HelloAsso)</Button>
        </a>
      )}
      <form onSubmit={submit} className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="join-org-form">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Prénom *</Label><Input required value={f.first_name} onChange={(e)=>setF({...f, first_name: e.target.value})}/></div>
          <div><Label>Nom *</Label><Input required value={f.last_name} onChange={(e)=>setF({...f, last_name: e.target.value})}/></div>
        </div>
        <div><Label>Email *</Label><Input required type="email" value={f.email} onChange={(e)=>setF({...f, email: e.target.value})} data-testid="join-org-email"/></div>
        <div>
          <Label>Mot de passe *</Label>
          <div className="mt-1 relative">
            <Input required type={showPwd ? "text" : "password"} value={f.password} onChange={(e)=>setF({...f, password: e.target.value})} className="pr-10" data-testid="join-org-password"/>
            <button type="button" onClick={()=>setShowPwd(v=>!v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                    aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    data-testid="join-org-password-toggle">
              {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
        </div>
        <div><Label>Téléphone</Label><Input value={f.phone} onChange={(e)=>setF({...f, phone: e.target.value})}/></div>
        <Button type="submit" disabled={busy} className="btn-shine text-white w-full" data-testid="join-org-submit">{busy ? "..." : `Rejoindre ${org.name}`}</Button>
        <p className="text-[11px] text-slate-500 text-center">En vous inscrivant, vous acceptez les CGU et la politique de confidentialité.</p>
      </form>
    </div>
  );
}
